import { IDynamicField } from "../Types/Products/IDynamicField";
import { IQuestionnaireQuestion } from "../Types/Products/IQuestionnaireQuestion";
import { CurateResource } from "../Types/Curate/CurateResource";
import { IGrade } from "../Types/IGrade";
import { SummariseResponse } from "../Types/Evaluate/SummariseResponse";

type BuildSummarisePromptArgs = {
  criteriaGuidance: string;
  questions: Array<{ question: string; answer: string }>;
  fields: IDynamicField[];
  availableTags: string[];
};

export function buildSummariseAssessmentPrompt({
  criteriaGuidance,
  questions,
  fields,
  availableTags,
}: BuildSummarisePromptArgs): string {
  const fieldIndicators = fields.map((field) => {
    const format = field.description ? ` Format (do not include this heading): (${field.description}) ` : "";
    const explanation = field.formatDescription
      ? ` Explanation (do not include this heading): (${field.formatDescription}) `
      : "";
    return `${field.title}${format}${explanation}`;
  });

  const dynamicFields = fields
    .map((field) => {
      const key = (field.title || "").replace(/\s/g, "");
      return `      "${key}": "Summary of ${(field.title || "").toLowerCase()}",`;
    })
    .join("\n");

  return `Instructions:
Act as a user who is completing their Self Assessment Review.

You are provided with the guidance for this Quality Assurance Framework:
${criteriaGuidance || "not provided"}

You are provided with a list of the questions and answers:
${JSON.stringify(questions, null, 2)}

Task:
Create a summary of the user's answers for these headings: ${fieldIndicators.join(", ")}.

Available Tags:
${availableTags.join(", ") || "(none)"}

Before writing the JSON, call recommend_curate_resources once. Pass topic as a short label for this criteria. Use only tiles that tool returns — never library files, uploaded evidence, or anything you were not given by that tool.

Response format:
1. Call recommend_curate_resources.
2. A fenced json object. Use British English. No commentary outside the fences.

\`\`\`json
{
${dynamicFields}
      "Grade": (integer)0-100,
      "Tags": (array of strings) - Key themes from Available Tags only, or empty array
}
\`\`\`

3. After the json fence, a fenced curate-resources block with selected tiles from the tool, most relevant first. Omit the block if nothing fits. Never put curate JSON inside the assessment object.`;
}

function normalizeAssessmentFieldKey(value: string): string {
  return value.replace(/[\s_\-]+/g, "").toLowerCase();
}

function extractAssessmentJsonText(answer: string): string | null {
  // Curate tiles appended after the assessment JSON must not participate in brace matching.
  const withoutCurate = stripCurateResourcesMarker(answer);
  const jsonFence = withoutCurate.match(/```json\s*([\s\S]*?)```/i);
  if (jsonFence?.[1]?.trim()) {
    return jsonFence[1].trim();
  }

  const anyFence = withoutCurate.match(/```\s*([\s\S]*?)```/);
  if (anyFence?.[1]?.trim()) {
    const fenced = anyFence[1].trim();
    if (fenced.startsWith("{")) {
      return fenced;
    }
  }

  const start = withoutCurate.indexOf("{");
  const end = withoutCurate.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  return withoutCurate.slice(start, end + 1).trim();
}

function lookupAssessmentFieldContent(
  parsed: Record<string, unknown>,
  fieldTitle: string
): string | undefined {
  const exactKey = fieldTitle.replace(/\s/g, "");
  const direct = parsed[exactKey] ?? parsed[fieldTitle];
  if (typeof direct === "string") return direct;

  const wanted = normalizeAssessmentFieldKey(fieldTitle);
  for (const [key, value] of Object.entries(parsed)) {
    if (normalizeAssessmentFieldKey(key) === wanted && typeof value === "string") {
      return value;
    }
  }
  return undefined;
}

export function parseSummariseAssessmentAnswer(
  answer: string,
  fields: IDynamicField[]
): SummariseResponse | null {
  const jsonText = extractAssessmentJsonText(answer);
  if (!jsonText) return null;

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(jsonText) as Record<string, unknown>;
  } catch {
    return null;
  }

  const gradeNum = Number(parsed.Grade ?? parsed.grade ?? 0);
  const grade: IGrade =
    gradeNum >= 90 ? "Blue" : gradeNum >= 70 ? "Green" : gradeNum >= 50 ? "Amber" : gradeNum > 0 ? "Red" : "Not Set";

  const tagsRaw = parsed.Tags ?? parsed.tags;
  const tags = Array.isArray(tagsRaw) ? tagsRaw.map(String) : [];

  const updatedFields = fields.map((field) => {
    const content =
      lookupAssessmentFieldContent(parsed, field.title || "") ??
      field.content ??
      "";
    return { ...field, content: String(content) };
  });

  return { fields: updatedFields, tags, grade };
}

/** Matches a fenced or bare curate-resources marker; closing fence optional. */
export function stripCurateResourcesMarker(answer: string): string {
  return takeCurateResourcesFromAnswer(answer).cleanText;
}

/**
 * Strip the agent fence and return any tiles parsed from it. The tiles are for diagnostics
 * only — the agent can name resources the client has no access to, so anything rendered must
 * come from the server's access-checked "resources" stream event.
 */
export function takeCurateResourcesFromAnswer(answer: string): {
  cleanText: string;
  resources: CurateResource[];
} {
  if (!answer) {
    return { cleanText: answer ?? "", resources: [] };
  }

  let remaining = answer;
  let resources: CurateResource[] = [];
  let span = findCurateResourcesSpan(remaining);
  while (span) {
    if (resources.length === 0) {
      resources = parseCurateResourcesJson(span.json);
    }
    remaining = remaining.slice(0, span.start) + remaining.slice(span.end);
    span = findCurateResourcesSpan(remaining);
  }

  return { cleanText: remaining.trimEnd(), resources };
}

function parseCurateResourcesJson(json: string): CurateResource[] {
  if (!json.trim()) return [];
  try {
    const raw = JSON.parse(json) as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => {
        const r = item as Record<string, unknown>;
        const id = String(r.id ?? "");
        if (!id) return null;
        const title = String(r.Title ?? r.title ?? "");
        const extension = r.extension ? String(r.extension) : undefined;
        const fileExt = (extension ?? title).match(/\.([a-z0-9]+)$/i)?.[1];
        const kind = fileExt
          ? "file"
          : r.kind
            ? String(r.kind)
            : undefined;
        return {
          id,
          Title: title,
          Description: String(r.Description ?? r.description ?? ""),
          providerId: r.providerId ? String(r.providerId) : undefined,
          kind,
          extension: extension ?? (fileExt ? `.${fileExt}` : undefined),
        } as CurateResource;
      })
      .filter((r): r is CurateResource => !!r);
  } catch {
    return [];
  }
}

function findCurateResourcesSpan(
  text: string
): { start: number; end: number; json: string } | null {
  const fenceOpen = /(?:```|~~~)curate-resources\b/i.exec(text);
  if (fenceOpen) {
    const array = readJsonArray(text, fenceOpen.index + fenceOpen[0].length);
    if (array) {
      return {
        start: fenceOpen.index,
        end: skipClosingFence(text, array.end),
        json: array.json,
      };
    }
  }

  const labelRe = /curate-resources/gi;
  let label: RegExpExecArray | null;
  while ((label = labelRe.exec(text))) {
    const before = label.index > 0 ? text[label.index - 1] : "";
    if (before && /[A-Za-z0-9_-]/.test(before)) {
      continue;
    }

    let after = label.index + label[0].length;
    while (after < text.length && text[after] === "`") after += 1;
    const array = readJsonArray(text, after);
    if (!array) continue;

    return {
      start: includeLeadingWhitespace(text, label.index),
      end: array.end,
      json: array.json,
    };
  }

  if (fenceOpen) {
    return { start: fenceOpen.index, end: text.length, json: "" };
  }

  return null;
}

function readJsonArray(
  text: string,
  from: number
): { json: string; end: number } | null {
  let i = from;
  while (i < text.length && /\s/.test(text[i])) i += 1;
  if (text[i] !== "[") return null;

  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let j = i; j < text.length; j += 1) {
    const c = text[j];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (c === "\\") {
        escaped = true;
        continue;
      }
      if (c === '"') inString = false;
      continue;
    }

    if (c === '"') {
      inString = true;
      continue;
    }
    if (c === "[") depth += 1;
    else if (c === "]") {
      depth -= 1;
      if (depth === 0) {
        return { json: text.slice(i, j + 1), end: j + 1 };
      }
    }
  }

  return null;
}

function skipClosingFence(text: string, from: number): number {
  let i = from;
  while (i < text.length && /\s/.test(text[i])) i += 1;
  if (
    (text.startsWith("```", i) || text.startsWith("~~~", i))
  ) {
    i += 3;
    while (i < text.length && (text[i] === "`" || text[i] === "~")) i += 1;
  }
  return i;
}

function includeLeadingWhitespace(text: string, index: number): number {
  let start = index;
  while (start > 0 && (text[start - 1] === " " || text[start - 1] === "\t")) {
    start -= 1;
  }
  if (start > 0 && (text[start - 1] === "\n" || text[start - 1] === "\r")) {
    start -= 1;
    if (start > 0 && text[start] === "\n" && text[start - 1] === "\r") {
      start -= 1;
    }
  }
  return start;
}

export type PromptQuestionLike = Pick<IQuestionnaireQuestion, "id" | "question" | "answer">;
